import React, { useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  Divider,
  IconButton,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";

import "./quotationActions.css";

import ForumOutlinedIcon from "@mui/icons-material/ForumOutlined";
import BlockOutlinedIcon from "@mui/icons-material/BlockOutlined";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import PriceChangeOutlinedIcon from "@mui/icons-material/PriceChangeOutlined";
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import RemoveIcon from "@mui/icons-material/Remove";
import AddIcon from "@mui/icons-material/Add";

const discussionReasons = [
  {
    value: "PRICE_OR_BUDGET",
    title: "Price or budget",
    description: "Discuss the total cost or your available budget.",
    icon: <PriceChangeOutlinedIcon />,
  },
  {
    value: "ITEMS_OR_QUANTITY",
    title: "Items or quantities",
    description: "Remove quotation items or adjust their quantities.",
    icon: <FormatListBulletedIcon />,
  },
  {
    value: "OTHER",
    title: "Other",
    description: "Discuss something else about this quotation.",
    icon: <ChatBubbleOutlineIcon />,
  },
];

const declineReasons = [
  {
    value: "PRICE_OR_BUDGET",
    title: "Price or budget",
    description: "The quotation is above my available budget.",
    icon: <PriceChangeOutlinedIcon />,
  },
  {
    value: "NOT_REQUIRED",
    title: "Not required anymore",
    description: "I no longer need the proposed work.",
    icon: <BlockOutlinedIcon />,
  },
  {
    value: "OTHER",
    title: "Other",
    description: "My reason is not listed above.",
    icon: <ChatBubbleOutlineIcon />,
  },
];

const QuotationActions = ({
  quotation,
  onAccept,
  accepting = false,
  disabled = false,
  onSubmitResponse,
  submittingResponse = false,
}) => {
  const [step, setStep] = useState(-1);
  const [selectedType, setSelectedType] = useState("");
  const [reason, setReason] = useState("");
  const [comments, setComments] = useState("");
  const [itemChanges, setItemChanges] = useState({});
  const [submitError, setSubmitError] = useState("");
  const [responseSubmitted, setResponseSubmitted] = useState(false);

  const lines = quotation?.lines || [];
  const currencySymbol = quotation?.currencySymbol || "";

  const isDiscussion = selectedType === "DISCUSSION_REQUEST";
  const isDeclined = selectedType === "DECLINED";
  const isItemRequest = isDiscussion && reason === "ITEMS_OR_QUANTITY";

  const isOtherReason = reason === "OTHER";
  const isCommentRequired = isOtherReason;

  const availableReasons = isDeclined ? declineReasons : discussionReasons;

  const selectedReason = availableReasons.find((item) => item.value === reason);

  const showReasonComments = isDeclined || (isDiscussion && !isItemRequest);

  const selectedChanges = Object.values(itemChanges);

  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined || amount === "") {
      return "-";
    }

    const number = Number(amount);

    if (!Number.isFinite(number)) {
      return "-";
    }

    return `${currencySymbol}${number.toFixed(2)}`;
  };

  const getItemTotal = (line) => {
    if (line.lineTotal !== null && line.lineTotal !== undefined) {
      return formatCurrency(line.lineTotal);
    }

    const quantity = Number(line.quantity);
    const unitPrice = Number(line.unitPrice);

    if (!Number.isFinite(quantity) || !Number.isFinite(unitPrice)) {
      return "-";
    }

    return formatCurrency(quantity * unitPrice);
  };

  const updateItemChange = (lineId, updates) => {
    setItemChanges((previous) => ({
      ...previous,
      [lineId]: {
        ...previous[lineId],
        ...updates,
      },
    }));
  };

  const toggleItem = (lineId, checked, line) => {
    setItemChanges((previous) => {
      if (!checked) {
        const updated = { ...previous };
        delete updated[lineId];
        return updated;
      }

      return {
        ...previous,
        [lineId]: {
          itemId: line.id,
          description: line.description || "Quotation item",
          currentQuantity: Number(line.quantity),
          action: "CHANGE_QUANTITY",
          requestedQuantity: "",
        },
      };
    });
  };

  const resetForm = () => {
    setStep(-1);
    setSelectedType("");
    setReason("");
    setComments("");
    setItemChanges({});
    setSubmitError("");
  };

  const handleClose = () => {
    if (submittingResponse) return;
    resetForm();
  };

  const handleContinue = (type) => {
    setSelectedType(type);
    setReason("");
    setComments("");
    setItemChanges({});
    setSubmitError("");
    setStep(2);
  };

  const handleBack = () => {
    if (submittingResponse) return;

    setSubmitError("");

    if (step === 2) {
      setStep(0);
      return;
    }

    if (step === 3) {
      setStep(2);
      return;
    }

    if (step === 4) {
      setStep(isItemRequest ? 3 : 2);
      return;
    }

    resetForm();
  };

  const handleReasonContinue = () => {
    setSubmitError("");

    if (!reason) return;

    if (isCommentRequired && !comments.trim()) {
      setSubmitError("Please add a comment for the selected reason.");
      return;
    }

    if (isItemRequest) {
      setStep(3);
    } else {
      setStep(4);
    }
  };

  const handleReviewContinue = () => {
    setSubmitError("");

    if (!reason) {
      setSubmitError("Please select a reason.");
      return;
    }

    if (isCommentRequired && !comments.trim()) {
      setSubmitError("Please add a comment for the selected reason.");
      return;
    }

    if (isItemRequest && Object.keys(itemChanges).length === 0) {
      setSubmitError("Please select at least one item you want to change.");
      return;
    }

    if (isItemRequest) {
      const invalidQuantity = Object.values(itemChanges).some((item) => {
        if (item.action === "REMOVE") return false;

        const requestedQuantity = item.requestedQuantity;

        return (
          requestedQuantity === "" ||
          !Number.isFinite(Number(requestedQuantity)) ||
          Number(requestedQuantity) < 0 ||
          Number(requestedQuantity) === Number(item.currentQuantity)
        );
      });

      if (invalidQuantity) {
        setSubmitError(
          "Enter a valid quantity different from the current quantity, or choose Remove item.",
        );
        return;
      }
    }

    setStep(4);
  };

  // Prepare the API payload and submit it through the parent page.
  const handleSubmit = async () => {
    if (submittingResponse || responseSubmitted) return;

    setSubmitError("");

    if (!reason) {
      setSubmitError("Please select a reason.");
      return;
    }

    if (isCommentRequired && !comments.trim()) {
      setSubmitError("Please add a comment for the selected reason.");
      return;
    }

    if (isItemRequest && Object.keys(itemChanges).length === 0) {
      setSubmitError("Please select at least one item you want to change.");
      return;
    }

    const requestedChanges = Object.values(itemChanges).map((item) => {
      if (item.action === "REMOVE") {
        return {
          itemId: item.itemId,
          action: "REMOVE",
        };
      }

      return {
        itemId: item.itemId,
        action: "CHANGE_QUANTITY",
        requestedQuantity: Number(item.requestedQuantity),
      };
    });

    const responseData = {
      decision: selectedType,
      reason,
      decisionNote: comments.trim(),
      requestedChanges: isItemRequest ? requestedChanges : [],
    };

    try {
      if (typeof onSubmitResponse !== "function") {
        throw new Error("Quotation response submission is not configured.");
      }

      await onSubmitResponse(responseData);

      // API request succeeded.
      setResponseSubmitted(true);
      setSubmitError("");
      setStep(5);
    } catch (error) {
      console.error("Quotation response submission failed:", error);

      setSubmitError(
        error?.response?.data?.message ||
          error?.message ||
          "Unable to submit your response. Please try again.",
      );
    }
  };

  const progressSteps = isItemRequest
    ? ["Response", "Reason", "Items", "Review"]
    : ["Response", "Reason", "Review"];

  const activeProgressIndex =
    step === 0
      ? 0
      : step === 2
        ? 1
        : step === 3
          ? 2
          : step === 4
            ? progressSteps.length - 1
            : -1;

  const dialogTitle = {
    0: "Respond to quotation",
    2: "Choose a reason",
    3: "Choose items to change",
    4: "Review your response",
    5: "Response submitted",
  }[step];

  const cardSx = (selected, danger = false) => ({
    width: "100%",
    textAlign: "left",
    display: "flex",
    alignItems: "center",
    gap: 1.5,
    p: 1.75,
    border: "1px solid",
    borderColor: selected ? (danger ? "#E7A4A4" : "#80B4FF") : "#E2EAF3",
    borderRadius: 2.5,
    bgcolor: selected ? (danger ? "#FFF5F5" : "#F0F6FF") : "#FFFFFF",
    cursor: "pointer",
    transition: "border-color 160ms ease, background-color 160ms ease",
    "&:hover": {
      borderColor: danger ? "#E7A4A4" : "#80B4FF",
      bgcolor: danger ? "#FFF8F8" : "#F7FAFF",
    },
  });

  return (
    <section className="quotation-action-section">
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={1.5}
        className="quotation-action-buttons"
      >
        <Button
          variant="contained"
          size="large"
          disabled={accepting || disabled || responseSubmitted}
          onClick={onAccept}
          className="quotation-accept-button"
        >
          {accepting ? "Accepting..." : "Accept Quotation"}
        </Button>

        <Button
          variant="outlined"
          color="error"
          size="large"
          disabled={accepting || disabled || responseSubmitted}
          onClick={() => {
            setSubmitError("");
            setStep(0);
          }}
          className="quotation-reject-button"
        >
          Discuss / Decline
        </Button>
      </Stack>

      <Dialog
        open={step >= 0}
        onClose={handleClose}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          className: "quotation-response-dialog",
          elevation: 0,
        }}
      >
        <Box className="quotation-dialog-heading">
          {step !== 0 && step !== 5 && (
            <IconButton
              onClick={handleBack}
              aria-label="Go back"
              className="quotation-dialog-back"
              size="small"
              disabled={submittingResponse}
            >
              <ArrowBackIcon />
            </IconButton>
          )}

          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="h6" className="quotation-dialog-title">
              {dialogTitle}
            </Typography>

            <Typography variant="body2" color="text.secondary">
              {step === 0 && "Choose how you would like to respond."}

              {step === 2 &&
                (isDeclined
                  ? "Choose why you do not want to proceed with this quotation."
                  : "Choose what you would like to discuss about this quotation.")}

              {step === 3 &&
                "Select the items and tell us what you would like to change."}

              {step === 4 && "Check your response before confirming."}

              {step === 5 && "Your response has been successfully submitted."}
            </Typography>
          </Box>

          {step !== 0 && step !== 5 && (
            <Chip
              size="small"
              label={`Step ${
                step === 2 ? 2 : step === 3 ? 3 : progressSteps.length
              } of ${progressSteps.length}`}
              className="quotation-step-chip"
            />
          )}
        </Box>

        {step !== 0 && step !== 5 && (
          <Box className="quotation-progress">
            {progressSteps.map((label, index) => (
              <Box
                key={label}
                className={`quotation-progress-step ${
                  index <= activeProgressIndex ? "is-active" : ""
                }`}
              >
                <Box className="quotation-progress-line" />
                <Typography variant="caption">{label}</Typography>
              </Box>
            ))}
          </Box>
        )}

        <DialogContent className="quotation-dialog-content">
          {submitError && step !== 5 && (
            <Alert
              severity="error"
              sx={{ mb: 2 }}
              onClose={() => setSubmitError("")}
            >
              {submitError}
            </Alert>
          )}

          {/* Step 1: Response type */}
          {step === 0 && (
            <Stack spacing={1.25}>
              <Box
                role="button"
                tabIndex={0}
                onClick={() => handleContinue("DISCUSSION_REQUEST")}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    handleContinue("DISCUSSION_REQUEST");
                  }
                }}
                sx={cardSx(false)}
              >
                <Box className="quotation-option-icon quotation-option-icon-blue">
                  <ForumOutlinedIcon />
                </Box>

                <Box sx={{ flex: 1 }}>
                  <Typography fontWeight={700}>
                    Discuss changes before deciding
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mt: 0.5 }}
                  >
                    I may proceed if we can agree on the changes.
                  </Typography>
                </Box>

                <CheckCircleOutlineIcon sx={{ color: "#A9B8C9" }} />
              </Box>

              <Box
                role="button"
                tabIndex={0}
                onClick={() => handleContinue("DECLINED")}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    handleContinue("DECLINED");
                  }
                }}
                sx={cardSx(false, true)}
              >
                <Box className="quotation-option-icon quotation-option-icon-red">
                  <BlockOutlinedIcon />
                </Box>

                <Box sx={{ flex: 1 }}>
                  <Typography fontWeight={700}>Decline quotation</Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mt: 0.5 }}
                  >
                    I do not want to proceed with this proposal.
                  </Typography>
                </Box>

                <CheckCircleOutlineIcon sx={{ color: "#A9B8C9" }} />
              </Box>
            </Stack>
          )}

          {/* Step 2: Reason and comments */}
          {step === 2 && (
            <Stack spacing={1.25}>
              {availableReasons.map((item) => {
                const selected = reason === item.value;

                return (
                  <Box
                    key={item.value}
                    role="button"
                    tabIndex={0}
                    onClick={() => {
                      setReason(item.value);
                      setSubmitError("");
                    }}
                    onKeyDown={(event) => {
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        setReason(item.value);
                        setSubmitError("");
                      }
                    }}
                    sx={cardSx(selected, isDeclined)}
                  >
                    <Box
                      className={`quotation-option-icon ${
                        selected
                          ? isDeclined
                            ? "quotation-option-icon-red"
                            : "quotation-option-icon-blue"
                          : "quotation-option-icon-neutral"
                      }`}
                    >
                      {item.icon}
                    </Box>

                    <Box sx={{ flex: 1 }}>
                      <Typography fontWeight={700}>{item.title}</Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mt: 0.5 }}
                      >
                        {item.description}
                      </Typography>
                    </Box>

                    {selected && (
                      <CheckCircleOutlineIcon
                        sx={{
                          color: isDeclined ? "#D65353" : "#2878E8",
                        }}
                      />
                    )}
                  </Box>
                );
              })}

              {showReasonComments && (
                <TextField
                  fullWidth
                  multiline
                  minRows={3}
                  maxRows={5}
                  label={`Additional comments${
                    isCommentRequired ? " (required)" : " (optional)"
                  }`}
                  placeholder={
                    isDeclined
                      ? "Tell us a little more about your decision..."
                      : "Tell us what you would like to discuss..."
                  }
                  value={comments}
                  inputProps={{ maxLength: 500 }}
                  onChange={(event) => setComments(event.target.value)}
                  helperText={`${comments.length}/500`}
                  required={isCommentRequired}
                />
              )}
            </Stack>
          )}

          {/* Step 3: Item selection */}
          {step === 3 && (
            <Stack spacing={1.25}>
              <Alert severity="info" icon={<InfoOutlinedIcon />}>
                Select an item to request a change. The original quotation will
                remain unchanged.
              </Alert>

              {lines.length > 0 ? (
                lines.map((line) => {
                  const change = itemChanges[line.id];
                  const selected = Boolean(change);

                  return (
                    <Box
                      key={line.id}
                      className={`quotation-item-card ${
                        selected ? "quotation-item-card-selected" : ""
                      }`}
                      onClick={() => {
                        if (!selected) {
                          toggleItem(line.id, true, line);
                        }
                      }}
                    >
                      <Box className="quotation-item-top">
                        <IconButton
                          size="small"
                          aria-label={
                            selected ? "Selected item" : "Select item"
                          }
                          className="quotation-item-selector"
                          onClick={(event) => {
                            event.stopPropagation();
                            toggleItem(line.id, !selected, line);
                          }}
                        >
                          {selected ? (
                            <CheckCircleOutlineIcon />
                          ) : (
                            <RadioButtonUncheckedIcon />
                          )}
                        </IconButton>

                        <Box className="quotation-item-summary">
                          <Typography className="quotation-item-name">
                            {line.description || "Quotation item"}
                          </Typography>

                          {line.details && (
                            <Typography className="quotation-item-details">
                              {line.details}
                            </Typography>
                          )}

                          <Typography className="quotation-item-pricing">
                            Qty: {line.quantity ?? "-"}
                            {" · "}
                            Unit: {formatCurrency(line.unitPrice)}
                            {" · "}
                            Total: <strong>{getItemTotal(line)}</strong>
                          </Typography>
                        </Box>

                        {selected && (
                          <Box
                            className="quotation-item-action-area"
                            onClick={(event) => event.stopPropagation()}
                          >
                            <ToggleButtonGroup
                              exclusive
                              size="small"
                              value={change.action}
                              onChange={(_, value) => {
                                if (!value) return;

                                updateItemChange(line.id, {
                                  action: value,
                                  requestedQuantity: "",
                                });
                              }}
                              aria-label="Requested item change"
                              className="quotation-item-action-toggle"
                            >
                              <ToggleButton value="CHANGE_QUANTITY">
                                Change quantity
                              </ToggleButton>

                              <ToggleButton value="REMOVE">
                                Remove item
                              </ToggleButton>
                            </ToggleButtonGroup>
                          </Box>
                        )}
                      </Box>

                      {selected && (
                        <Box
                          className="quotation-item-controls"
                          onClick={(event) => event.stopPropagation()}
                        >
                          {change.action === "CHANGE_QUANTITY" && (
                            <Box className="quotation-quantity-control">
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                Requested quantity
                              </Typography>

                              <Stack
                                direction="row"
                                alignItems="center"
                                spacing={0.5}
                              >
                                <IconButton
                                  size="small"
                                  aria-label="Decrease quantity"
                                  disabled={
                                    change.requestedQuantity === "" ||
                                    Number(change.requestedQuantity) <= 0
                                  }
                                  onClick={() =>
                                    updateItemChange(line.id, {
                                      requestedQuantity: String(
                                        Math.max(
                                          0,
                                          Number(change.requestedQuantity) - 1,
                                        ),
                                      ),
                                    })
                                  }
                                >
                                  <RemoveIcon fontSize="small" />
                                </IconButton>

                                <TextField
                                  size="small"
                                  type="number"
                                  value={change.requestedQuantity}
                                  onChange={(event) =>
                                    updateItemChange(line.id, {
                                      requestedQuantity: event.target.value,
                                    })
                                  }
                                  inputProps={{
                                    min: 0,
                                    step: "any",
                                    "aria-label": "Requested quantity",
                                  }}
                                  className="quotation-quantity-input"
                                />

                                <IconButton
                                  size="small"
                                  aria-label="Increase quantity"
                                  onClick={() =>
                                    updateItemChange(line.id, {
                                      requestedQuantity: String(
                                        change.requestedQuantity === ""
                                          ? 1
                                          : Number(change.requestedQuantity) +
                                              1,
                                      ),
                                    })
                                  }
                                >
                                  <AddIcon fontSize="small" />
                                </IconButton>

                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  Current: {line.quantity ?? "-"}
                                </Typography>
                              </Stack>
                            </Box>
                          )}

                          {change.action === "REMOVE" && (
                            <Alert
                              severity="warning"
                              icon={<RemoveCircleOutlineIcon />}
                              className="quotation-remove-alert"
                            >
                              You are requesting removal of this item.
                            </Alert>
                          )}
                        </Box>
                      )}
                    </Box>
                  );
                })
              ) : (
                <Alert severity="info">
                  No quotation items are available to change.
                </Alert>
              )}

              <TextField
                fullWidth
                multiline
                minRows={2}
                maxRows={4}
                label="Additional comments (optional)"
                placeholder="Add any other details about the requested item changes..."
                value={comments}
                inputProps={{ maxLength: 500 }}
                onChange={(event) => setComments(event.target.value)}
                helperText={`${comments.length}/500`}
              />
            </Stack>
          )}

          {/* Step 4: Review */}
          {step === 4 && (
            <Stack spacing={1.5}>
              <Box className="quotation-review-panel">
                <Typography variant="subtitle2" color="text.secondary">
                  Response
                </Typography>
                <Typography fontWeight={700} sx={{ mt: 0.5 }}>
                  {isDiscussion
                    ? "Discuss changes before deciding"
                    : "Decline quotation"}
                </Typography>
              </Box>

              <Box className="quotation-review-panel">
                <Typography variant="subtitle2" color="text.secondary">
                  Reason
                </Typography>
                <Typography fontWeight={700} sx={{ mt: 0.5 }}>
                  {selectedReason?.title || reason}
                </Typography>
              </Box>

              {isItemRequest && (
                <Box className="quotation-review-panel">
                  <Typography
                    variant="subtitle2"
                    color="text.secondary"
                    sx={{ mb: 1 }}
                  >
                    Requested changes
                  </Typography>

                  <Stack spacing={1}>
                    {selectedChanges.map((item) => (
                      <Box key={item.itemId} className="quotation-review-item">
                        <Typography fontWeight={600}>
                          {item.description}
                        </Typography>

                        <Typography variant="body2" color="text.secondary">
                          {item.action === "REMOVE"
                            ? `Remove item · Current quantity: ${item.currentQuantity}`
                            : `Change quantity from ${item.currentQuantity} to ${item.requestedQuantity}`}
                        </Typography>
                      </Box>
                    ))}
                  </Stack>
                </Box>
              )}

              {comments.trim() && (
                <Box className="quotation-review-panel">
                  <Typography variant="subtitle2" color="text.secondary">
                    Additional comments
                  </Typography>
                  <Typography sx={{ mt: 0.75, whiteSpace: "pre-wrap" }}>
                    {comments.trim()}
                  </Typography>
                </Box>
              )}

              <Alert severity="info" icon={<InfoOutlinedIcon />}>
                This response will not automatically change the original
                quotation.
              </Alert>
            </Stack>
          )}

          {/* Step 5: API success confirmation */}
          {step === 5 && (
            <Box className="quotation-success-content">
              <Box className="quotation-success-icon">
                <CheckCircleOutlineIcon />
              </Box>

              <Typography variant="h5" fontWeight={700} textAlign="center">
                {isDiscussion
                  ? "Discussion request submitted"
                  : "Quotation declined successfully"}
              </Typography>

              <Typography color="text.secondary" textAlign="center">
                {isDiscussion
                  ? "Your discussion request has been sent to the company. They will review your request and contact you."
                  : "You have declined this quotation. Thank you for letting us know."}
              </Typography>

              <Box className="quotation-status-panel">
                {/* Response type */}
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  spacing={1}
                >
                  <Typography variant="body2">Response type</Typography>

                  <Chip
                    size="small"
                    label={isDiscussion ? "Discussion Request" : "Declined"}
                    color={isDiscussion ? "info" : "error"}
                  />
                </Stack>

                <Divider sx={{ my: 1.5 }} />

                {/* Reason / Requested changes */}
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  spacing={1}
                >
                  <Typography variant="body2">
                    {isItemRequest ? "Requested changes" : "Reason"}
                  </Typography>

                  <Typography variant="body2" fontWeight={600}>
                    {selectedReason?.title || reason}
                  </Typography>
                </Stack>

                <Divider sx={{ my: 1.5 }} />

                {/* Quotation status */}
                <Typography variant="h5" fontWeight={700} textAlign="center">
                  {isDiscussion
                    ? "Discussion request submitted successfully"
                    : "Quotation declined successfully"}
                </Typography>

                <Typography color="text.secondary" textAlign="center">
                  {isDiscussion
                    ? "Your discussion request has been submitted. The company will review your feedback and may contact you with a revised quotation."
                    : "You have declined this quotation. The company has been notified of your decision."}
                </Typography>
              </Box>
            </Box>
          )}
        </DialogContent>

        <DialogActions className="quotation-dialog-actions">
          {step === 0 && (
            <Button onClick={handleClose} color="inherit">
              Cancel
            </Button>
          )}

          {step === 2 && (
            <>
              <Button variant="outlined" onClick={handleBack}>
                Back
              </Button>

              <Button
                variant="contained"
                onClick={handleReasonContinue}
                disabled={!reason || (isCommentRequired && !comments.trim())}
              >
                Continue
              </Button>
            </>
          )}

          {step === 3 && (
            <>
              <Button variant="outlined" onClick={handleBack}>
                Back
              </Button>

              <Button
                variant="contained"
                onClick={handleReviewContinue}
                disabled={
                  isItemRequest && Object.keys(itemChanges).length === 0
                }
              >
                Continue
              </Button>
            </>
          )}

          {step === 4 && (
            <>
              <Button
                variant="outlined"
                onClick={handleBack}
                disabled={submittingResponse}
              >
                Back
              </Button>

              <Button
                variant="contained"
                color={isDeclined ? "error" : "primary"}
                onClick={handleSubmit}
                disabled={submittingResponse}
              >
                {submittingResponse
                  ? "Submitting..."
                  : isDeclined
                    ? "Confirm Decline"
                    : "Confirm Response"}
              </Button>
            </>
          )}

          {step === 5 && (
            <Button variant="contained" onClick={handleClose} fullWidth>
              Back to quotation
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </section>
  );
};

export default QuotationActions;
